import React from 'react';
import { observer } from 'mobx-react-lite';
import {
  CalendarIcon,
  DrawingPinIcon,
  FileIcon,
  GearIcon,
  GridIcon,
  Link2Icon,
} from '@radix-ui/react-icons';
import { OverviewIcon, Person } from '@producer/ui-kit';

import { NavMenu, NavMenuItem } from '../../../components/nav-menu';
import { uiStore } from '../../../core/stores/ui-store';
import { useCurrentProject } from '../../../hooks/use-current-project';
import { projectPath, projectTabPath } from '../../../app/paths';
import { Tooltip } from '../../../components/tooltip/Tooltip';
import {
  ClipboardTick,
  MaximizeMenuIcon,
  MinimizeMenuIcon,
  TheatreMasksIcon,
} from '../../../components/icons';

import styles from './project-menu.module.scss';
type ProjectMenuProps = {
  isOpen: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  onSelect?: () => void;
};

export const ProjectMenu: React.FC<ProjectMenuProps> = observer(
  ({ isOpen, setIsOpen, onSelect }) => {
    const project = useCurrentProject();

    if (!project) {
      return null;
    }

    return (
      <div className={styles['container']} data-expand={isOpen}>
        <div className={styles['menu-content']}>
          <NavMenu>
            <Tooltip text="Overview" position="right" disabled={isOpen}>
              <NavMenuItem
                to={projectPath(project.spaceId, project._id)}
                icon={<OverviewIcon />}
                label="Overview"
                onClick={onSelect}
                isActive={uiStore.currentLocation === 'project.overview'}
              />
            </Tooltip>

            <Tooltip text="Documents" position="right" disabled={isOpen}>
              <NavMenuItem
                to={projectTabPath(project.spaceId, project._id, 'documents')}
                icon={<FileIcon />}
                label="Documents"
                onClick={onSelect}
                isActive={uiStore.currentLocation === 'project.documents'}
              />
            </Tooltip>

            <Tooltip text="Tasks" position="right" disabled={isOpen}>
              <NavMenuItem
                to={projectTabPath(project.spaceId, project._id, 'tasks')}
                icon={<ClipboardTick />}
                label="Tasks"
                onClick={onSelect}
                isActive={uiStore.currentLocation === 'project.tasks'}
              />
            </Tooltip>

            <Tooltip text="Calendar" position="right" disabled={isOpen}>
              <NavMenuItem
                to={projectTabPath(project.spaceId, project._id, 'calendar')}
                icon={<CalendarIcon />}
                label="Calendar"
                onClick={onSelect}
                isActive={uiStore.currentLocation === 'project.calendar'}
              />
            </Tooltip>

            <Tooltip text="Shared Links" position="right" disabled={isOpen}>
              <NavMenuItem
                to={projectTabPath(project.spaceId, project._id, 'share-links')}
                icon={<Link2Icon />}
                label="Shared Links"
                isActive={uiStore.currentLocation === 'project.share-links'}
                onClick={onSelect}
              />
            </Tooltip>

            <Tooltip text="Settings" position="right" disabled={isOpen}>
              <NavMenuItem
                to={projectTabPath(project.spaceId, project._id, 'settings')}
                icon={<GearIcon />}
                label="Settings"
                onClick={onSelect}
                isActive={uiStore.currentLocation === 'project.settings'}
              />
            </Tooltip>

            <div className={styles['subtitle']} data-expand={isOpen}>
              Creation
            </div>

            <Tooltip text="Storyboards" position="right" disabled={isOpen}>
              <NavMenuItem
                to={projectTabPath(project.spaceId, project._id, 'storyboards')}
                icon={<GridIcon />}
                label="Storyboards"
                onClick={onSelect}
                isActive={uiStore.currentLocation === 'project.storyboards'}
              />
            </Tooltip>

            <div className={styles['subtitle']} data-expand={isOpen}>
              Resources
            </div>

            <Tooltip text="Crew" position="right" disabled={isOpen}>
              <NavMenuItem
                to={projectTabPath(project.spaceId, project._id, 'crew')}
                icon={<Person />}
                label="Crew"
                isActive={uiStore.currentLocation === 'project.crew'}
                onClick={onSelect}
              />
            </Tooltip>

            <Tooltip text="Locations" position="right" disabled={isOpen}>
              <NavMenuItem
                to={projectTabPath(project.spaceId, project._id, 'locations')}
                icon={<DrawingPinIcon />}
                label="Locations"
                isActive={uiStore.currentLocation === 'project.locations'}
                onClick={onSelect}
              />
            </Tooltip>

            <Tooltip text="Characters" position="right" disabled={isOpen}>
              <NavMenuItem
                to={projectTabPath(project.spaceId, project._id, 'characters')}
                icon={<TheatreMasksIcon />}
                label="Characters"
                isActive={uiStore.currentLocation === 'project.characters'}
                onClick={onSelect}
              />
            </Tooltip>
          </NavMenu>
        </div>

        <div style={{ flex: 1 }} />

        <div className={styles['footer']}>
          {setIsOpen && (
            <button className={styles['menu-toggle-button']} onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? <MinimizeMenuIcon /> : <MaximizeMenuIcon />}
            </button>
          )}
        </div>
      </div>
    );
  },
);
